import React, { useEffect, useState } from "react";
import { DatePicker, Alert } from "antd";
import dayjs from "dayjs";
import { ListCard, ListTable } from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { PHARMACY_PAGE_SIZE } from "../common/ListScreen";
import { getPharmacyNonMovingStockListApi } from "../../../services/pharmacyDashboard.services";
import { formatAmount } from "../common/money";

const { RangePicker } = DatePicker;

const PAGE_SIZE = PHARMACY_PAGE_SIZE;


const stockValue = (record) =>
  Number(record?.selling_price || 0) * Math.max(0, Number(record?.available_qty || 0));

const columns = [
  {
    title: "Medicine",
    dataIndex: "pharmacy_medicine_name",
    key: "pharmacy_medicine_name",
  },
  { title: "Batch Number", dataIndex: "batch_number", key: "batch_number" },
  {
    title: "Expiry",
    dataIndex: "expiry",
    key: "expiry",
    render: (value) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
  },
  {
    title: "Manufactured",
    dataIndex: "manufacturing_date",
    key: "manufacturing_date",
    render: (value) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
  },
  {
    title: "Available Qty",
    dataIndex: "available_qty",
    key: "available_qty",
    align: "right",
    render: (value) => Number(value ?? 0),
  },
  {
    title: "MRP",
    dataIndex: "mrp",
    key: "mrp",
    align: "right",
    render: (value) => formatAmount(value || 0),
  },
  {
    title: "Rate",
    dataIndex: "selling_price",
    key: "selling_price",
    align: "right",
    render: (value) => formatAmount(value || 0),
  },
  {
    title: "Purchase Rate",
    dataIndex: "purchase_rate",
    key: "purchase_rate",
    align: "right",
    render: (value) => formatAmount(value || 0),
  },
  {
    title: "Value",
    key: "value",
    align: "right",
    render: (_, record) => formatAmount(stockValue(record)),
  },
];

const NonMovingStockTable = ({ onLoaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [range, setRange] = useState([
    dayjs().subtract(90, "day"),
    dayjs(),
  ]);

  const from = range?.[0];
  const to = range?.[1];

  useEffect(() => {
    if (!from || !to) {
      setData(null);
      setError("Select a date range to see non-moving stock.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    const queryString = new URLSearchParams({
      from: from.format("YYYY-MM-DD"),
      to: to.format("YYYY-MM-DD"),
      page,
      limit: PAGE_SIZE,
    });

    getPharmacyNonMovingStockListApi(queryString)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data);
        onLoaded?.(res?.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            "Could not load non-moving stock. Please try again."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    /* `onLoaded` only hands the response upward; re-running the fetch because
       its identity moved would be a call for nothing. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, from, to]);

  return (
    <ListCard
      title="Non-Moving Stock"
      range={{ page, pageSize: PHARMACY_PAGE_SIZE, total: Number(data?.count) || 0 }}
      count={Number(data?.count) || 0}
      toolbar={
        <RangePicker
          allowClear={false}
          value={range}
          onChange={(value) => {
            setRange(value);
            setPage(1);
          }}
        />
      }
    >
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}
      <ListTable
        loading={loading}
        dataSource={data?.results || []}
        columns={columns}
        rowKey="id"
        pagination={getTablePagination({
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.count ?? 0,
          onChange: (p) => setPage(p),
        })}
      />
    </ListCard>
  );
};

export default NonMovingStockTable;
